<template>
  <v-container
    id="tenant-details"
    fluid
    tag="section"
  >
    <!-- Breadcrumb -->
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center mb-4">
          <v-btn
            icon
            @click="$router.push('/')"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-breadcrumbs
            :items="breadcrumbs"
            class="pa-0 pl-2"
          >
            <template v-slot:divider>
              <v-icon>mdi-chevron-right</v-icon>
            </template>
          </v-breadcrumbs>
        </div>
      </v-col>
    </v-row>

    <!-- Cards de Estatísticas -->
    <v-row>
      <!-- Admins -->
      <v-col cols="12" sm="6" lg="4">
        <base-material-stats-card
          color="info"
          icon="mdi-shield-account"
          title="Admins"
          :value="String(stats.admins)"
          sub-icon="mdi-account-star"
          sub-text="Administradores"
        />
      </v-col>

      <!-- Gestores -->
      <v-col cols="12" sm="6" lg="4">
        <base-material-stats-card
          color="success"
          icon="mdi-account-tie"
          title="Gestores"
          :value="String(stats.managers)"
          sub-icon="mdi-account-supervisor"
          sub-text="Gerentes de equipe"
        />
      </v-col>

      <!-- Usuários -->
      <v-col cols="12" sm="6" lg="4">
        <base-material-stats-card
          color="primary"
          icon="mdi-account-group"
          title="Usuários"
          :value="String(stats.totalUsers)"
          sub-icon="mdi-account-multiple"
          sub-text="Total de colaboradores"
        />
      </v-col>
    </v-row>

    <!-- Informações Cadastrais -->
    <v-row>
      <v-col cols="12">
        <base-material-card
          color="orange"
          icon="mdi-domain"
          title="Informações Cadastrais"
          class="px-5 py-3"
        >
          <template v-slot:after-heading>
            <div class="d-flex">
              <v-spacer />
              <v-btn
                color="success"
                class="mr-2"
                @click="editTenant"
              >
                <v-icon left>mdi-pencil</v-icon>
                Editar
              </v-btn>
              <v-btn
                color="info"
                @click="generateInvoice"
              >
                <v-icon left>mdi-file-document</v-icon>
                Gerar Nova Fatura
              </v-btn>
            </div>
          </template>

          <v-card-text>
            <v-row v-if="loading">
              <v-col cols="12" class="text-center py-12">
                <v-progress-circular
                  indeterminate
                  color="orange"
                  size="64"
                />
                <div class="mt-4 text-h6">Carregando informações...</div>
              </v-col>
            </v-row>

            <v-row v-else>
              <v-col cols="12" md="6">
                <div class="d-flex align-center mb-4">
                  <v-avatar
                    :color="tenant.avatarUrl ? 'transparent' : getTenantColor(tenant.name)"
                    size="80"
                    class="mr-4"
                  >
                    <v-img
                      v-if="tenant.avatarUrl"
                      :src="tenant.avatarUrl"
                      alt="Logo"
                    />
                    <span
                      v-else
                      class="white--text text-h4"
                    >
                      {{ getTenantInitials(tenant.name) }}
                    </span>
                  </v-avatar>
                  <div>
                    <div class="text-h5 font-weight-bold">{{ tenant.name }}</div>
                    <div class="text-caption grey--text">{{ tenant.id }}</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="6" class="text-md-right">
                <v-chip
                  :color="tenant.status === 'ACTIVE' ? 'success' : 'grey'"
                  dark
                  large
                >
                  {{ tenant.status === 'ACTIVE' ? 'Ativo' : 'Inativo' }}
                </v-chip>
              </v-col>

              <v-col cols="12">
                <v-divider class="my-4" />
              </v-col>

              <!-- Informações em Grid -->
              <v-col cols="12" md="6">
                <v-list dense>
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon color="grey">mdi-domain</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-subtitle>Nome</v-list-item-subtitle>
                      <v-list-item-title class="text-h6">
                        {{ tenant.name || '-' }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon color="grey">mdi-email</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-subtitle>E-mail</v-list-item-subtitle>
                      <v-list-item-title>
                        {{ tenantCompanyEmail || '-' }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon color="grey">mdi-phone</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-subtitle>Telefone</v-list-item-subtitle>
                      <v-list-item-title>
                        {{ tenantCompanyPhone || '-' }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon color="grey">mdi-map-marker</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-subtitle>Endereço</v-list-item-subtitle>
                      <v-list-item-title>
                        {{ tenantCompanyAddress || '-' }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>

              <v-col cols="12" md="6">
                <v-list dense>
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon color="grey">mdi-package-variant</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-subtitle>Plano</v-list-item-subtitle>
                      <v-list-item-title>
                        <v-chip
                          :color="getPlanColor(tenant.plan)"
                          small
                          dark
                        >
                          {{ tenant.plan || 'Enterprise' }}
                        </v-chip>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon color="grey">mdi-calendar-start</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-subtitle>Data Início</v-list-item-subtitle>
                      <v-list-item-title>
                        {{ formatDate(tenant.startDate || tenant.createdAt) }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon :color="getExpirationColor(tenant.subscriptionExpiresAt)">
                        mdi-calendar-clock
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-subtitle>Data Vencimento</v-list-item-subtitle>
                      <v-list-item-title :class="getExpirationClass(tenant.subscriptionExpiresAt)">
                        {{ formatDate(tenant.subscriptionExpiresAt) }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon color="grey">mdi-account-multiple</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-subtitle>Limite de Usuários</v-list-item-subtitle>
                      <v-list-item-title>
                        {{ stats.totalUsers }} / {{ tenant.maxUsers || 'Ilimitado' }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
          </v-card-text>
        </base-material-card>
      </v-col>
    </v-row>

    <!-- Atividades Recentes -->
    <v-row>
      <v-col cols="12">
        <base-material-card
          color="purple"
          icon="mdi-history"
          title="Atividades Recentes"
          class="px-5 py-3"
        >
          <v-card-text>
            <!-- Se houver atividades -->
            <v-timeline
              v-if="activities.length > 0"
              dense
              class="ml-4"
            >
              <v-timeline-item
                v-for="(activity, index) in activities"
                :key="index"
                :color="activity.color"
                small
              >
                <template v-slot:icon>
                  <v-icon
                    dark
                    small
                  >
                    {{ activity.icon }}
                  </v-icon>
                </template>

                <v-card class="elevation-2">
                  <v-card-text>
                    <div class="d-flex justify-space-between">
                      <div>
                        <div class="font-weight-medium">
                          {{ activity.description }}
                        </div>
                        <div class="text-caption grey--text mt-1">
                          {{ activity.actor }}
                        </div>
                      </div>
                      <div class="text-caption grey--text">
                        {{ activity.time }}
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-timeline-item>
            </v-timeline>

            <!-- Mensagem quando não há atividades -->
            <div
              v-else
              class="text-center py-12"
            >
              <v-icon
                size="64"
                color="grey lighten-1"
              >
                mdi-history
              </v-icon>
              <p class="text-h6 grey--text text--lighten-1 mt-4 mb-2">
                Nada por aqui ainda
              </p>
              <p class="text-body-2 grey--text">
                As atividades deste tenant aparecerão aqui conforme forem realizadas.
              </p>
            </div>

            <!-- Botão "Ver todas" - só aparece se houver atividades -->
            <div
              v-if="activities.length > 0"
              class="text-center mt-4"
            >
              <v-btn
                text
                color="purple"
                @click="viewAllActivities"
              >
                Ver todas atividades
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </v-card-text>
        </base-material-card>
      </v-col>
    </v-row>
    <!-- ============= DIALOG DE EDIÇÃO UNIFICADO ============= -->
    <v-dialog
      v-model="showEditDialog"
      max-width="640"
      persistent
      scrollable
    >
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center justify-space-between pb-3">
          <span class="text-h6 font-weight-bold">Editar Tenant</span>
          <v-btn
            icon
            small
            @click="closeEditDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-2 pb-4">
          <!-- Loading -->
          <div
            v-if="editLoading"
            class="text-center py-12"
          >
            <v-progress-circular
              indeterminate
              color="primary"
              size="48"
            />
            <div class="mt-4 text-body-1 grey--text">Carregando dados...</div>
          </div>

          <template v-else>
            <!-- Upload Avatar/Logo -->
            <div class="text-center mb-6">
              <div class="d-inline-block" style="position: relative">
                <v-avatar
                  :color="editPreviewAvatar ? 'transparent' : getTenantColor(editForm.name)"
                  size="100"
                  class="elevation-2"
                >
                  <v-progress-circular
                    v-if="uploadingEditAvatar"
                    :size="50"
                    :width="5"
                    color="primary"
                    indeterminate
                  />
                  <v-img
                    v-else-if="editPreviewAvatar"
                    :src="editPreviewAvatar"
                    alt="Logo"
                  />
                  <span
                    v-else
                    class="white--text text-h3"
                  >
                    {{ getTenantInitials(editForm.name) }}
                  </span>
                </v-avatar>

                <v-btn
                  fab
                  x-small
                  color="white"
                  class="elevation-3"
                  style="position: absolute; bottom: 0; right: 0"
                  :loading="uploadingEditAvatar"
                  :disabled="uploadingEditAvatar"
                  @click="$refs.editFileInput && $refs.editFileInput.click()"
                >
                  <v-icon small color="primary">mdi-camera</v-icon>
                </v-btn>

                <input
                  ref="editFileInput"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="onEditFileSelected"
                >
              </div>
            </div>

            <!-- Nome -->
            <v-text-field
              v-model="editForm.name"
              label="Nome da Empresa *"
              outlined
              dense
              class="mb-4"
            />

            <!-- Plano — Cards visuais -->
            <div class="text-body-2 font-weight-medium mb-3">Plano</div>
            <v-row class="mb-4">
              <v-col
                v-for="plan in planDetails"
                :key="plan.value"
                cols="4"
              >
                <v-card
                  :outlined="editForm.plan !== plan.value"
                  :elevation="editForm.plan === plan.value ? 6 : 0"
                  class="pa-3 text-center cursor-pointer"
                  :style="editForm.plan === plan.value
                    ? 'border-width: 2px; min-height: 135px; transition: all 0.3s; background: #303F9F !important; color: white !important;'
                    : 'border-width: 2px; min-height: 135px; transition: all 0.3s;'"
                  hover
                  @click="selectEditPlan(plan.value)"
                >
                  <v-icon
                    :color="editForm.plan === plan.value ? 'white' : 'indigo'"
                    class="mb-2"
                    size="26"
                  >
                    {{ plan.icon }}
                  </v-icon>
                  <div class="text-body-2 font-weight-bold mb-1">{{ plan.name }}</div>
                  <div
                    class="text-caption"
                    :class="editForm.plan === plan.value ? 'white--text' : 'grey--text'"
                  >
                    {{ plan.users }}
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Limite de Usuários -->
            <v-text-field
              v-model.number="editForm.maxUsers"
              label="Limite de Usuários (opcional)"
              type="number"
              outlined
              dense
              :min="editPlanLimits.min"
              :max="editPlanLimits.max"
              :hint="editPlanLimits.hint"
              persistent-hint
            />

            <!-- Campos extras -->
            <v-row class="mt-2">
              <v-col cols="12">
                <v-text-field
                  v-model="editForm.email"
                  label="E-mail de Contato"
                  outlined
                  dense
                  prepend-inner-icon="mdi-email"
                />
              </v-col>
            </v-row>

            <!-- Dados da Empresa (companyData) -->
            <v-divider class="my-4" />
            <div class="d-flex align-center mb-3">
              <v-icon small color="blue-grey darken-1" class="mr-2">mdi-office-building-cog</v-icon>
              <span class="text-body-2 font-weight-medium">Dados da Empresa</span>
            </div>

            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editForm.razaoSocial"
                  label="Razão Social"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editForm.nomeFantasia"
                  label="Nome Fantasia"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editForm.cnpj"
                  label="CNPJ"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editForm.setor"
                  label="Setor"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editForm.telefone"
                  label="Telefone da Empresa"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editForm.website"
                  label="Website"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editForm.endereco"
                  label="Endereço"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editForm.porteEmpresa"
                  :items="['MEI', 'ME', 'EPP', 'Média', 'Grande']"
                  label="Porte da Empresa"
                  outlined
                  dense
                  clearable
                />
              </v-col>
            </v-row>

            <!-- Favicon -->
            <v-divider class="my-4" />
            <div class="text-body-2 font-weight-medium mb-2">Favicon / Ícone do Menu</div>
            <div class="text-caption grey--text mb-3">
              Este ícone aparecerá no menu lateral (drawer) dos usuários e gestores.
            </div>

            <div class="d-flex align-center mb-4">
              <v-avatar
                :color="editPreviewFavicon ? 'transparent' : 'grey lighten-3'"
                size="48"
                class="elevation-1 mr-4"
                rounded
              >
                <v-progress-circular
                  v-if="uploadingEditFavicon"
                  :size="24"
                  :width="3"
                  color="primary"
                  indeterminate
                />
                <v-img
                  v-else-if="editPreviewFavicon"
                  :src="editPreviewFavicon"
                  alt="Favicon"
                  contain
                />
                <v-icon
                  v-else
                  color="grey"
                  size="24"
                >
                  mdi-image-outline
                </v-icon>
              </v-avatar>

              <div class="flex-grow-1">
                <v-btn
                  small
                  outlined
                  color="primary"
                  :loading="uploadingEditFavicon"
                  :disabled="uploadingEditFavicon"
                  @click="$refs.editFaviconInput && $refs.editFaviconInput.click()"
                >
                  <v-icon small left>mdi-upload</v-icon>
                  {{ editPreviewFavicon ? 'Trocar Favicon' : 'Enviar Favicon' }}
                </v-btn>
                <div class="text-caption grey--text mt-1">PNG ou SVG, até 2MB. Recomendado: 64×64px.</div>
              </div>

              <input
                ref="editFaviconInput"
                type="file"
                accept="image/*"
                style="display: none"
                @change="onEditFaviconSelected"
              >
            </div>

            <!-- Info Sistema (somente leitura) -->
            <div
              v-if="editMeta.isolationType || editMeta.databaseName"
              class="mb-2"
            >
              <v-divider class="mb-4" />
              <div class="text-subtitle-2 font-weight-bold mb-3">
                <v-icon left small color="grey">mdi-database</v-icon>
                Informações do Sistema
              </div>
              <v-row>
                <v-col
                  v-if="editMeta.isolationType"
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    :value="editMeta.isolationType"
                    label="Tipo de Isolamento"
                    outlined
                    dense
                    readonly
                    disabled
                    prepend-inner-icon="mdi-shield-lock"
                  />
                </v-col>
                <v-col
                  v-if="editMeta.databaseName"
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    :value="editMeta.databaseName"
                    label="Nome do Banco"
                    outlined
                    dense
                    readonly
                    disabled
                    prepend-inner-icon="mdi-database"
                  />
                </v-col>
              </v-row>
            </div>

            <!-- Seção: Administradores -->
            <v-divider class="mb-4" />
            <div class="d-flex align-center mb-3">
              <div class="text-subtitle-2 font-weight-bold">
                <v-icon left small color="info">mdi-shield-account</v-icon>
                Administradores
              </div>
              <v-spacer />
              <v-chip small outlined color="info">
                {{ editAdmins.length }} admin{{ editAdmins.length !== 1 ? 's' : '' }}
              </v-chip>
            </div>

            <div
              v-if="editAdmins.length === 0"
              class="text-center py-6"
            >
              <v-icon size="48" color="grey lighten-2">mdi-account-off</v-icon>
              <div class="text-body-2 grey--text mt-2">Nenhum administrador encontrado</div>
            </div>

            <v-card
              v-for="(admin, idx) in editAdmins"
              :key="admin.id || idx"
              outlined
              class="mb-3 pa-4"
            >
              <v-row align="center">
                <v-col cols="auto">
                  <v-avatar
                    :color="admin.avatarUrl ? undefined : 'info'"
                    size="48"
                  >
                    <v-img
                      v-if="admin.avatarUrl"
                      :src="admin.avatarUrl"
                    />
                    <span
                      v-else
                      class="white--text text-h6"
                    >
                      {{ getAdminInitials(admin.name) }}
                    </span>
                  </v-avatar>
                </v-col>

                <v-col>
                  <v-row dense>
                    <v-col cols="12" md="5">
                      <v-text-field
                        v-model="admin.name"
                        label="Nome"
                        outlined
                        dense
                        hide-details="auto"
                        prepend-inner-icon="mdi-account"
                      />
                    </v-col>
                    <v-col cols="12" md="5">
                      <v-text-field
                        v-model="admin.email"
                        label="E-mail"
                        outlined
                        dense
                        hide-details="auto"
                        prepend-inner-icon="mdi-email"
                        :rules="[v => !!v || 'E-mail obrigatório', v => /.+@.+\..+/.test(v) || 'E-mail inválido']"
                      />
                    </v-col>
                    <v-col cols="12" md="2" class="d-flex align-center justify-center">
                      <v-chip
                        v-if="admin.status"
                        :color="admin.status === 'ACTIVE' ? 'success' : 'warning'"
                        x-small
                        dark
                      >
                        {{ admin.status === 'ACTIVE' ? 'Ativo' : 'Pendente' }}
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-col>

                <v-col cols="auto" class="d-flex align-center">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        :color="admin.email !== admin._originalEmail ? 'warning' : 'info'"
                        :loading="resendingEmailId === admin.id"
                        :disabled="resendingEmailId !== null"
                        v-bind="attrs"
                        v-on="on"
                        @click="resendWelcomeEmail(admin)"
                      >
                        <v-icon>mdi-email-fast</v-icon>
                      </v-btn>
                    </template>
                    <span v-if="admin.email !== admin._originalEmail">
                      Reenviar e trocar e-mail para {{ admin.email }}
                    </span>
                    <span v-else>Reenviar e-mail de boas-vindas</span>
                  </v-tooltip>
                </v-col>
              </v-row>

              <div
                v-if="admin.firebaseUid"
                class="text-caption grey--text mt-2 ml-14"
              >
                <v-icon x-small color="grey" class="mr-1">mdi-key</v-icon>
                Firebase UID: {{ admin.firebaseUid }}
              </div>
            </v-card>
          </template>
        </v-card-text>

        <v-divider />

        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn
            text
            :disabled="savingEdit"
            @click="closeEditDialog"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            depressed
            :loading="savingEdit"
            :disabled="editLoading"
            class="px-5"
            @click="saveEditData"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Salvar Alterações
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Crop de Imagem (Edit Mode) -->
    <v-dialog
      v-model="editCropDialog"
      max-width="600"
      persistent
    >
      <v-card>
        <v-card-title class="text-h5">
          <v-icon left color="primary">mdi-crop</v-icon>
          Ajustar Logo do Tenant
        </v-card-title>

        <v-card-text>
          <div class="crop-container">
            <div style="max-height: 400px">
              <img
                ref="editCropperImage"
                :src="editImageToCrop"
                style="max-width: 100%"
              >
            </div>
          </div>
          <v-alert
            text
            color="info"
            icon="mdi-information"
            class="mt-4"
          >
            <small>
              Ajuste a área de corte para 1:1 (quadrado).
              A imagem será redimensionada automaticamente para 500×500px.
            </small>
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelEditCrop">Cancelar</v-btn>
          <v-btn
            color="primary"
            :loading="uploadingEditAvatar"
            @click="confirmEditCrop"
          >
            <v-icon left>mdi-check</v-icon>
            Confirmar e Enviar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { TenantsService } from '@/services'
import TenantAvatarService from '@/services/tenant-avatar.service'
import Cropper from 'cropperjs'

export default {
  name: 'TenantDetails',

  data: () => ({
    tenantId: null,
    tenant: {},
    stats: {
      admins: 0,
      managers: 0,
      totalUsers: 0,
    },
    activities: [],
    loading: false,

    // === Edit dialog ===
    showEditDialog: false,
    editLoading: false,
    savingEdit: false,
    resendingEmailId: null,
    editForm: {
      name: '',
      email: '',
      address: '',
      plan: '',
      maxUsers: null,
      // companyData
      razaoSocial: '',
      nomeFantasia: '',
      cnpj: '',
      setor: '',
      telefone: '',
      endereco: '',
      website: '',
      porteEmpresa: '',
    },
    editMeta: {
      isolationType: '',
      databaseName: '',
    },
    editAdmins: [],

    // Upload avatar/logo
    editPreviewAvatar: null,
    uploadingEditAvatar: false,
    editCropDialog: false,
    editImageToCrop: null,
    editCropper: null,
    editSelectedFile: null,

    // Upload favicon
    editPreviewFavicon: null,
    uploadingEditFavicon: false,

    // Plano
    planDetails: [
      {
        value: 'STARTER',
        name: 'Starter',
        icon: 'mdi-rocket-launch',
        users: 'Até 10 usuários',
      },
      {
        value: 'STANDARD',
        name: 'Standard',
        icon: 'mdi-office-building',
        users: 'De 11 até 100 usuários',
      },
      {
        value: 'ENTERPRISE',
        name: 'Enterprise',
        icon: 'mdi-domain',
        users: 'Acima de 100 usuários',
      },
    ],

    breadcrumbs: [
      {
        text: 'Dashboard',
        disabled: false,
        to: '/',
      },
      {
        text: 'Tenants',
        disabled: false,
        to: '/',
      },
      {
        text: 'Carregando...',
        disabled: true,
      },
    ],
  }),

  async mounted() {
    this.tenantId = this.$route.params.id
    await this.loadTenantData()

    // Auto-abrir dialog se vier do DashboardOwner com ?edit=1
    if (this.$route.query.edit === '1') {
      this.editTenant()
      // Limpar query param sem recarregar
      this.$router.replace({ query: {} }).catch(() => {})
    }
  },

  computed: {
    editPlanLimits() {
      const limits = {
        STARTER: { min: 1, max: 10, hint: 'Plano Starter: de 1 até 10 usuários' },
        STANDARD: { min: 11, max: 100, hint: 'Plano Standard: de 11 até 100 usuários' },
        ENTERPRISE: { min: 101, max: null, hint: 'Plano Enterprise: acima de 100 usuários (ilimitado)' },
      }
      return limits[this.editForm.plan] || { min: 1, max: null, hint: '' }
    },

    // Computed helpers para exibir dados reais da tenant (companyData)
    tenantCompanyEmail() {
      const cd = this.tenant.companyData
      return (cd && cd.email) || this.tenant.adminEmail || this.tenant.email || ''
    },
    tenantCompanyPhone() {
      const cd = this.tenant.companyData
      return (cd && cd.telefone) || this.tenant.phone || ''
    },
    tenantCompanyAddress() {
      const cd = this.tenant.companyData
      return (cd && cd.endereco) || this.tenant.address || ''
    },
  },

  methods: {
    async loadTenantData() {
      this.loading = true
      try {
        // Carregar informações do tenant e estatísticas em paralelo
        const [tenantResponse, statsResponse] = await Promise.all([
          TenantsService.getById(this.tenantId),
          TenantsService.getStats(this.tenantId),
        ])

        this.tenant = tenantResponse.data
        this.stats = statsResponse.data

        // Atualizar breadcrumb
        this.breadcrumbs[2] = {
          text: this.tenant.name,
          disabled: true,
        }

        // Carregar atividades (mock por enquanto)
        this.loadActivities()
      } catch (error) {
        console.error('Erro ao carregar dados do tenant:', error)
        this.$notify({
          type: 'error',
          text: 'Erro ao carregar informações do tenant',
        })
        // Dados mock em caso de erro
        this.loadMockData()
      } finally {
        this.loading = false
      }
    },

    async loadActivities() {
      try {
        const response = await TenantsService.getActivities(this.tenantId, 10)
        const data = response.data
        
        console.log('📊 Atividades recebidas do backend:', data)
        
        // Verificar se há atividades
        if (!data.activities || data.activities.length === 0) {
          console.log('ℹ️ Nenhuma atividade encontrada para este tenant')
          this.activities = []
          return
        }
        
        // Mapear para formato do frontend
        this.activities = data.activities.map(activity => {
          // Extrair o "ator" - compatível com duas versões do backend:
          // Versão 1 (ideal): activity.actor = {id, name, email}
          // Versão 2 (atual): extrair da description
          let actorName = 'Sistema'
          
          if (activity.actor && activity.actor.name) {
            // Backend retorna objeto actor completo (versão ideal)
            actorName = activity.actor.name
          } else if (activity.description) {
            // Backend não retorna actor, extrair da description (versão atual)
            // Exemplos:
            // "lgsbroglia@gmail.com criou o usuário Joao teste"
            // "Luis Broglia adicionou Thiago R. como gestor"
            const match = activity.description.match(/^([^\s]+(?:\s+[^\s]+)?)\s+(criou|adicionou|atualizou|removeu|se cadastrou|completou)/)
            if (match) {
              actorName = match[1]
            }
          }
          
          return {
            description: activity.description,
            actor: actorName,
            time: this.formatTimeAgo(activity.createdAt),
            icon: activity.icon || 'mdi-information',
            color: activity.color || 'grey',
          }
        })
        
        console.log('✅ Atividades mapeadas:', this.activities)
      } catch (error) {
        console.error('❌ Erro ao carregar atividades:', error)
        // Em caso de erro, não mostrar nada (mais profissional)
        this.activities = []
      }
    },

    formatTimeAgo(isoDate) {
      if (!isoDate) return '-'
      
      const date = new Date(isoDate)
      const now = new Date()
      const diffMs = now - date
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffMinutes = Math.floor(diffMs / (1000 * 60))
      
      if (diffMinutes < 1) return 'Agora mesmo'
      if (diffMinutes < 60) return `há ${diffMinutes} minuto${diffMinutes > 1 ? 's' : ''}`
      if (diffHours < 24) return `há ${diffHours} hora${diffHours > 1 ? 's' : ''}`
      if (diffDays === 0) return 'Hoje'
      if (diffDays === 1) return 'há 1 dia atrás'
      if (diffDays < 7) return `${diffDays} dias atrás'`
      if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7)
        return `${weeks} semana${weeks > 1 ? 's' : ''} atrás`
      }
      const months = Math.floor(diffDays / 30)
      return `${months} ${months > 1 ? 'meses' : 'mês'} atrás`
    },

    loadMockData() {
      this.tenant = {
        id: this.tenantId,
        name: 'Tenant A',
        status: 'ACTIVE',
        plan: 'Enterprise',
        email: 'contact@tenant-a.com',
        phone: '+55 11 98765-4321',
        address: 'Av. Paulista, 1000, São Paulo, SP, 01310-100, Brasil',
        subscriptionExpiresAt: '2024-04-28',
        createdAt: '2022-03-15',
        maxUsers: 200,
      }

      this.stats = {
        admins: 3,
        managers: 5,
        totalUsers: 124,
      }

      this.breadcrumbs[2] = {
        text: this.tenant.name,
        disabled: true,
      }

      this.loadActivities()
    },

    // === AÇÕES ===
    async editTenant() {
      this.showEditDialog = true
      this.editLoading = true
      try {
        const response = await TenantsService.getEditData(this.tenantId)
        const data = response.data

        const t = data.tenant || data
        const companyData = t.companyData || {}
        this.editForm = {
          name: t.name || this.tenant.name || '',
          email: companyData.email || t.adminEmail || t.email || this.tenant.email || '',
          address: companyData.endereco || t.address || this.tenant.address || '',
          plan: t.plan || this.tenant.plan || '',
          maxUsers: t.maxUsers || this.tenant.maxUsers || null,
          // companyData fields
          razaoSocial: companyData.razaoSocial || '',
          nomeFantasia: companyData.nomeFantasia || '',
          cnpj: companyData.cnpj || '',
          setor: companyData.setor || '',
          telefone: companyData.telefone || '',
          endereco: companyData.endereco || '',
          website: companyData.website || '',
          porteEmpresa: companyData.porteEmpresa || '',
        }

        this.editMeta = {
          isolationType: data.isolationType || '',
          databaseName: data.databaseName || '',
        }

        // Avatar / Favicon
        this.editPreviewAvatar = t.avatarUrl || this.tenant.avatarUrl || null
        this.editPreviewFavicon = t.logoUrl || this.tenant.logoUrl || null

        // Admins — deep-copy para permitir edição local + guardar email original
        this.editAdmins = (data.admins || []).map(a => ({ ...a, _originalEmail: a.email }))
      } catch (error) {
        console.error('Erro ao carregar dados de edição:', error)
        const fallbackCD = (this.tenant.companyData) || {}
        this.editForm = {
          name: this.tenant.name || '',
          email: fallbackCD.email || this.tenant.email || '',
          address: fallbackCD.endereco || this.tenant.address || '',
          plan: this.tenant.plan || '',
          maxUsers: this.tenant.maxUsers || null,
          razaoSocial: fallbackCD.razaoSocial || '',
          nomeFantasia: fallbackCD.nomeFantasia || '',
          cnpj: fallbackCD.cnpj || '',
          setor: fallbackCD.setor || '',
          telefone: fallbackCD.telefone || '',
          endereco: fallbackCD.endereco || '',
          website: fallbackCD.website || '',
          porteEmpresa: fallbackCD.porteEmpresa || '',
        }
        this.editMeta = { isolationType: '', databaseName: '' }
        this.editPreviewAvatar = this.tenant.avatarUrl || null
        this.editPreviewFavicon = this.tenant.logoUrl || null
        this.editAdmins = []
        this.$notify({
          type: 'warning',
          text: 'Não foi possível carregar todos os dados. Algumas informações podem estar incompletas.',
        })
      } finally {
        this.editLoading = false
      }
    },

    closeEditDialog() {
      this.showEditDialog = false
      this.editForm = {
        name: '', email: '', address: '', plan: '', maxUsers: null,
        razaoSocial: '', nomeFantasia: '', cnpj: '', setor: '',
        telefone: '', endereco: '', website: '', porteEmpresa: '',
      }
      this.editMeta = { isolationType: '', databaseName: '' }
      this.editAdmins = []
      this.editPreviewAvatar = null
      this.editPreviewFavicon = null
      this.editSelectedFile = null
      this.editImageToCrop = null
      if (this.editCropper) {
        this.editCropper.destroy()
        this.editCropper = null
      }
    },

    // === Plan selection ===
    selectEditPlan(planValue) {
      this.editForm.plan = planValue
      const defaults = { STARTER: 10, STANDARD: 50, ENTERPRISE: 250 }
      this.editForm.maxUsers = defaults[planValue] || 10
    },

    // === Upload Avatar/Logo (with crop) ===
    onEditFileSelected(event) {
      const file = event.target.files[0]
      if (!file) return
      if (!file.type.startsWith('image/')) {
        this.$notify({ type: 'warning', text: 'Selecione um arquivo de imagem válido' })
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$notify({ type: 'warning', text: 'Arquivo muito grande (máximo 5MB)' })
        return
      }
      this.editSelectedFile = file
      const reader = new FileReader()
      reader.onload = (e) => {
        this.editImageToCrop = e.target.result
        this.editCropDialog = true
        this.$nextTick(() => this.initEditCropper())
      }
      reader.readAsDataURL(file)
      event.target.value = ''
    },

    initEditCropper() {
      if (this.editCropper) {
        this.editCropper.destroy()
      }
      const image = this.$refs.editCropperImage
      if (!image) return
      this.editCropper = new Cropper(image, {
        aspectRatio: 1,
        viewMode: 2,
        dragMode: 'move',
        autoCropArea: 0.8,
        restore: false,
        guides: true,
        center: true,
        highlight: true,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false,
        minCropBoxWidth: 100,
        minCropBoxHeight: 100,
      })
    },

    cancelEditCrop() {
      if (this.editCropper) {
        this.editCropper.destroy()
        this.editCropper = null
      }
      this.editCropDialog = false
      this.editImageToCrop = null
      this.editSelectedFile = null
    },

    async confirmEditCrop() {
      if (!this.editCropper) return
      const canvas = this.editCropper.getCroppedCanvas({
        width: 500,
        height: 500,
        imageSmoothingQuality: 'high',
      })
      canvas.toBlob(async (blob) => {
        if (!blob) return
        const croppedFile = new File([blob], this.editSelectedFile.name, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        })
        this.editPreviewAvatar = canvas.toDataURL('image/jpeg', 0.9)
        this.editCropDialog = false
        if (this.editCropper) {
          this.editCropper.destroy()
          this.editCropper = null
        }
        await this.uploadEditAvatar(croppedFile)
      }, 'image/jpeg', 0.9)
    },

    async uploadEditAvatar(file) {
      this.uploadingEditAvatar = true
      try {
        const res = await TenantAvatarService.uploadAvatar(file, this.tenantId)
        const avatarUrl = res.data
        this.editPreviewAvatar = avatarUrl
        this.tenant.avatarUrl = avatarUrl
      } catch (err) {
        console.error('Erro ao enviar avatar:', err)
        this.$notify({ type: 'error', text: 'Erro ao enviar logo do tenant' })
      } finally {
        this.uploadingEditAvatar = false
      }
    },

    // === Upload Favicon ===
    onEditFaviconSelected(event) {
      const file = event.target.files[0]
      if (!file) return
      if (!file.type.startsWith('image/')) {
        this.$notify({ type: 'warning', text: 'Selecione um arquivo de imagem válido' })
        return
      }
      if (file.size > 2 * 1024 * 1024) {
        this.$notify({ type: 'warning', text: 'Arquivo muito grande (máximo 2MB)' })
        return
      }
      const reader = new FileReader()
      reader.onload = (e) => {
        this.editPreviewFavicon = e.target.result
      }
      reader.readAsDataURL(file)
      this.uploadEditFavicon(file)
      event.target.value = ''
    },

    async uploadEditFavicon(file) {
      this.uploadingEditFavicon = true
      try {
        const res = await TenantAvatarService.uploadFavicon(file, this.tenantId)
        const faviconUrl = res.data
        this.editPreviewFavicon = faviconUrl
        this.tenant.logoUrl = faviconUrl
      } catch (err) {
        console.error('Erro ao enviar favicon:', err)
        this.$notify({ type: 'error', text: 'Erro ao enviar favicon do tenant' })
      } finally {
        this.uploadingEditFavicon = false
      }
    },

    async saveEditData() {
      this.savingEdit = true
      try {
        const f = this.editForm
        const payload = {
          tenant: {
            name: f.name,
            plan: f.plan,
            maxUsers: f.maxUsers,
            companyData: {
              razaoSocial: f.razaoSocial || null,
              nomeFantasia: f.nomeFantasia || null,
              cnpj: f.cnpj || null,
              setor: f.setor || null,
              telefone: f.telefone || null,
              email: f.email || null,
              endereco: f.endereco || f.address || null,
              website: f.website || null,
              porteEmpresa: f.porteEmpresa || null,
            },
          },
          admins: this.editAdmins.map(a => ({
            id: a.id,
            name: a.name,
            email: a.email,
          })),
        }

        await TenantsService.saveEditData(this.tenantId, payload)

        // Fechar dialog imediatamente após sucesso
        this.closeEditDialog()

        this.$notify({
          type: 'success',
          text: 'Tenant atualizado com sucesso!',
        })

        // Recarregar dados da página em background
        this.loadTenantData()
      } catch (error) {
        console.error('Erro ao salvar edição:', error)
        const msg = error.response?.data?.message || 'Erro ao salvar alterações'
        this.$notify({ type: 'error', text: msg })
      } finally {
        this.savingEdit = false
      }
    },

    async resendWelcomeEmail(admin) {
      // Se o email foi alterado no campo, enviar no body para o backend trocar
      const emailChanged = admin.email && admin._originalEmail && admin.email !== admin._originalEmail
      const body = emailChanged ? { email: admin.email } : {}

      this.resendingEmailId = admin.id
      try {
        const response = await TenantsService.resendWelcomeEmail(this.tenantId, admin.id, body)
        const data = response.data || {}

        // Atualizar email original se foi trocado com sucesso
        if (data.emailChanged === true || data.emailChanged === 'true') {
          admin._originalEmail = data.email || admin.email
          this.$notify({
            type: 'success',
            text: `E-mail alterado e boas-vindas enviado para ${data.email || admin.email}`,
          })
        } else {
          this.$notify({
            type: 'success',
            text: `E-mail de boas-vindas reenviado para ${admin.email}`,
          })
        }
      } catch (error) {
        console.error('Erro ao reenviar e-mail:', error)
        const status = error.response?.status
        let msg = error.response?.data?.message || 'Erro ao reenviar e-mail de boas-vindas'

        if (status === 409) {
          msg = `O e-mail ${admin.email} já está em uso por outro usuário`
        } else if (status === 404) {
          msg = 'Administrador não encontrado'
        }

        this.$notify({ type: 'error', text: msg })
      } finally {
        this.resendingEmailId = null
      }
    },

    getAdminInitials(name) {
      if (!name) return '?'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },

    generateInvoice() {
      console.log('Gerar fatura para tenant:', this.tenant)
      // TODO: Abrir modal de geração de fatura
      this.$notify({
        type: 'info',
        text: 'Funcionalidade de geração de fatura em desenvolvimento',
      })
    },

    viewAllActivities() {
      console.log('Ver todas atividades do tenant:', this.tenantId)
      // TODO: Navegar para tela de atividades completa ou abrir modal
      this.$notify({
        type: 'info',
        text: 'Funcionalidade em desenvolvimento',
      })
    },

    // === HELPERS ===
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('pt-BR')
    },

    getExpirationColor(dateString) {
      if (!dateString) return 'grey'
      const today = new Date()
      const expiration = new Date(dateString)
      const diffDays = Math.ceil((expiration - today) / (1000 * 60 * 60 * 24))

      if (diffDays < 0) return 'red'
      if (diffDays <= 7) return 'orange'
      if (diffDays <= 30) return 'warning'
      return 'success'
    },

    getExpirationClass(dateString) {
      const color = this.getExpirationColor(dateString)
      return `${color}--text font-weight-medium`
    },

    getPlanColor(plan) {
      const colors = {
        Pro: 'primary',
        Enterprise: 'purple',
        Standard: 'grey',
      }
      return colors[plan] || 'grey'
    },

    getTenantColor(name) {
      if (!name) return 'grey'
      const colors = ['primary', 'success', 'info', 'warning', 'error', 'purple']
      const index = name.charCodeAt(0) % colors.length
      return colors[index]
    },

    getTenantInitials(name) {
      if (!name) return '?'
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },
  },
}
</script>

<style scoped>
#tenant-details {
  min-height: 100vh;
}

.v-timeline-item >>> .v-timeline-item__body {
  width: 100%;
}
</style>
